import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import swal from '../../utils/swal';
import './Register.css';

const ERROR_MESSAGES = {
    USUARIO_DUPLICADO:        'El nombre de usuario ya está en uso.',
    MATRICULA_DUPLICADA:      'La matrícula ya está registrada.',
    OBRA_SOCIAL_NO_ENCONTRADA: 'La obra social indicada no existe.',
    PLAN_NO_ENCONTRADO:       'El plan indicado no existe en esa obra social.',
};

function Register() {
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [rol,  setRol]  = useState('paciente');

    // Paso 1 — datos personales
    const [nombre,           setNombre]           = useState('');
    const [dni,              setDni]              = useState('');
    const [obraSocialNombre, setObraSocialNombre] = useState('');
    const [planNombre,       setPlanNombre]       = useState('');
    const [nombreMedico,     setNombreMedico]     = useState('');
    const [matricula,        setMatricula]        = useState('');

    // Paso 2 — credenciales
    const [nombreUsuario, setNombreUsuario] = useState('');
    const [password,      setPassword]      = useState('');

    const [error,   setError]   = useState('');
    const [loading, setLoading] = useState(false);

    function handleRolChange(nuevoRol) {
        setRol(nuevoRol);
        setStep(1);
        setError('');
        setNombre('');
        setDni('');
        setObraSocialNombre('');
        setPlanNombre('');
        setNombreMedico('');
        setMatricula('');
    }

    function handleContinuar(e) {
        e.preventDefault();
        setError('');

        const camposVacios = rol === 'paciente'
            ? [nombre, dni, obraSocialNombre, planNombre].some(v => !v.trim())
            : [nombreMedico, matricula].some(v => !v.trim());

        if (camposVacios) {
            setError('Por favor completá todos los campos.');
            return;
        }

        setStep(2);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (rol === 'paciente') {
                await axiosInstance.post('/pacientes', {
                    nombre:        nombre.trim(),
                    password,
                    nombreUsuario: nombreUsuario.trim(),
                    dni:           Number(dni),
                    obraSocial:    obraSocialNombre.trim(),
                    plan:          planNombre.trim(),
                });
            } else {
                await axiosInstance.post('/medicos', {
                    nombre:        nombreMedico.trim(),
                    password,
                    nombreUsuario: nombreUsuario.trim(),
                    matricula:     matricula.trim(),
                });
            }

            await swal.fire({
                icon:              'success',
                title:             '¡Registro exitoso!',
                text:              'Ya podés iniciar sesión con tu cuenta.',
                confirmButtonText: 'Ir al login',
            });
            navigate('/login');
        } catch (err) {
            const codigo  = err.response?.data?.error;
            const mensaje = ERROR_MESSAGES[codigo]
                ?? err.response?.data?.message
                ?? 'No se pudo completar el registro. Intentá de nuevo.';
            setError(mensaje);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="register">
            <div className="register__card">
                <div className="register__brand">SweetMedical</div>
                <h1 className="register__title">Crear cuenta</h1>

                {/* ── Paso 1: datos personales ── */}
                {step === 1 && (
                    <>
                        <div className="register__role-selector" role="group" aria-label="Tipo de usuario">
                            <button
                                type="button"
                                className={`register__role-btn ${rol === 'paciente' ? 'register__role-btn--active' : ''}`}
                                onClick={() => handleRolChange('paciente')}
                            >
                                Paciente
                            </button>
                            <button
                                type="button"
                                className={`register__role-btn ${rol === 'medico' ? 'register__role-btn--active' : ''}`}
                                onClick={() => handleRolChange('medico')}
                            >
                                Médico
                            </button>
                        </div>

                        <form className="register__form" onSubmit={handleContinuar} noValidate>
                            {rol === 'paciente' && (
                                <>
                                    <div className="register__field">
                                        <label className="register__label" htmlFor="nombre">Nombre completo</label>
                                        <input
                                            id="nombre"
                                            className="register__input"
                                            type="text"
                                            value={nombre}
                                            onChange={e => setNombre(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="register__field">
                                        <label className="register__label" htmlFor="dni">DNI</label>
                                        <input
                                            id="dni"
                                            className="register__input"
                                            type="number"
                                            value={dni}
                                            onChange={e => setDni(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="register__field">
                                        <label className="register__label" htmlFor="obraSocial">Obra social</label>
                                        <input
                                            id="obraSocial"
                                            className="register__input"
                                            type="text"
                                            value={obraSocialNombre}
                                            onChange={e => setObraSocialNombre(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="register__field">
                                        <label className="register__label" htmlFor="plan">Plan</label>
                                        <input
                                            id="plan"
                                            className="register__input"
                                            type="text"
                                            value={planNombre}
                                            onChange={e => setPlanNombre(e.target.value)}
                                            required
                                        />
                                    </div>
                                </>
                            )}

                            {rol === 'medico' && (
                                <>
                                    <div className="register__field">
                                        <label className="register__label" htmlFor="nombreMedico">Nombre completo</label>
                                        <input
                                            id="nombreMedico"
                                            className="register__input"
                                            type="text"
                                            value={nombreMedico}
                                            onChange={e => setNombreMedico(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="register__field">
                                        <label className="register__label" htmlFor="matricula">Matrícula</label>
                                        <input
                                            id="matricula"
                                            className="register__input"
                                            type="text"
                                            value={matricula}
                                            onChange={e => setMatricula(e.target.value)}
                                            required
                                        />
                                    </div>
                                </>
                            )}

                            {error && <p className="register__error" role="alert">{error}</p>}

                            <button className="register__btn" type="submit">
                                Continuar
                            </button>
                        </form>
                    </>
                )}

                {/* ── Paso 2: credenciales ── */}
                {step === 2 && (
                    <form className="register__form" onSubmit={handleSubmit} noValidate>
                        <div className="register__field">
                            <label className="register__label" htmlFor="nombreUsuario">Nombre de usuario</label>
                            <input
                                id="nombreUsuario"
                                className="register__input"
                                type="text"
                                value={nombreUsuario}
                                onChange={e => setNombreUsuario(e.target.value)}
                                autoComplete="username"
                                autoFocus
                                required
                            />
                        </div>

                        <div className="register__field">
                            <label className="register__label" htmlFor="password">Contraseña</label>
                            <input
                                id="password"
                                className="register__input"
                                type="password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                autoComplete="new-password"
                                required
                            />
                        </div>

                        {error && <p className="register__error" role="alert">{error}</p>}

                        <button className="register__btn" type="submit" disabled={loading}>
                            {loading ? 'Registrando...' : 'Registrarse'}
                        </button>

                        <button
                            type="button"
                            className="register__back-btn"
                            onClick={() => { setStep(1); setError(''); }}
                        >
                            ← Volver
                        </button>
                    </form>
                )}

                <p className="register__footer">
                    ¿Ya tenés cuenta?{' '}
                    <Link to="/login" className="register__link">Iniciá sesión</Link>
                </p>
            </div>
        </div>
    );
}

export default Register;
